/**
 * Forgot Password Page
 *
 * Allows users to request a password reset code via Cognito.
 */
import { useState } from 'react';

import { useFormWithValidation } from '@splitfiesta/core';
import { useNamespace, useTranslation } from '@splitfiesta/i18n';
import {
  Alert,
  Card,
  FormButton,
  FormField,
  H1,
  H2,
  Text,
} from '@splitfiesta/ui';

import { AuthPageSkeleton } from '@/components/common/AuthPageSkeleton';
import { RouteChangeLink } from '@/components/common/RouteChangeLink';
import { Footer } from '@/components/layout/Footer';

import { useAuth } from '@/hooks/useAuth';
import { useErrorHandler } from '@/hooks/useErrorHandler';

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');

  const { t, ready } = useTranslation();
  const { handleError } = useErrorHandler('auth');
  const { forgotPassword, isSendingResetCode, forgotPasswordError } = useAuth();

  // Load required namespaces
  const authLoaded = useNamespace('auth');
  const commonLoaded = useNamespace('common');
  const errorsLoaded = useNamespace('errors');

  // Form validation and submission
  const {
    handleSubmit: handleFormSubmit,
    isSubmitting,
    error,
    setError,
  } = useFormWithValidation({
    onSubmit: async () => {
      forgotPassword({ email });
    },
    validateFn: () => {
      if (!email.trim()) {
        return t('auth:errors.emailRequired');
      }
      return null;
    },
  });

  // Handle errors
  if (forgotPasswordError && !error) {
    setError(handleError(forgotPasswordError));
  }

  const isLoading = isSubmitting || isSendingResetCode;

  // Show skeleton while translations load
  if (!ready || !authLoaded || !commonLoaded || !errorsLoaded) {
    return <AuthPageSkeleton fieldCount={1} />;
  }

  return (
    <div className="min-h-screen bg-linear-to-br from-orange-400 to-red-400 dark:bg-slate-900 dark:bg-none flex flex-col">
      <div className="flex-1 p-4 pt-4">
        <div className="max-w-md w-full mx-auto">
          {/* Logo */}
          <div className="text-center mb-8 mt-8">
            <RouteChangeLink href="/">
              <H1 className="text-white hover:text-white/90 transition-colors cursor-pointer drop-shadow-lg mb-2">
                {t('common:app.name')}
              </H1>
            </RouteChangeLink>
          </div>

          <Card>
            <div className="text-center mb-6">
              <H2>{t('auth:forgotPassword.title')}</H2>
              <Text variant="bodySmall" className="mt-1">
                {t('auth:forgotPassword.subtitle')}
              </Text>
            </div>

            {error && (
              <Alert
                type="error"
                message={String(error)}
                className="mb-6"
                data-testid="forgot-password-error-message"
              />
            )}

            <form
              onSubmit={e => handleFormSubmit(e, { email })}
              className="space-y-4"
              data-testid="forgot-password-form"
            >
              <FormField
                id="email"
                type="email"
                label={t('common:forms.email')}
                value={email}
                onChange={setEmail}
                placeholder={t('auth:forgotPassword.emailPlaceholder')}
                required
                autoFocus
                disabled={isLoading}
                autoComplete="email"
                data-testid="forgot-password-email-input"
              />

              <FormButton
                type="submit"
                loading={isLoading}
                loadingText={t('common:actions.loading')}
                fullWidth
                data-testid="forgot-password-submit-button"
              >
                {t('auth:forgotPassword.submit')}
              </FormButton>
            </form>

            <div className="mt-6 text-center">
              <RouteChangeLink
                href="/login"
                className="text-slate-600 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-300 text-sm"
                data-testid="back-to-login-link"
              >
                {t('auth:forgotPassword.backToLogin')}
              </RouteChangeLink>
            </div>
          </Card>
        </div>
      </div>
      <Footer variant="minimal" />
    </div>
  );
}
